import React from 'react';
import type {ComponentType} from 'react';

import styled from '@emotion/styled';
import {Form} from '@nfq/react-form';
import {Row} from '@nfq/react-grid';

import {FilterTags} from '../FilterTags/FilterTags';

import {useFilterWrapper} from './useFilterWrapper';
import {processChildren} from './utils';

import type {WithChildren} from '../../../utils/childTypes';
import type {ThemeColor} from '@nfq/colors';

/**
 * The `FilterConfig` interface defines the configuration structure for dashboard filters.
 * Each filter specifies its behavior, default state, and available options.
 */
export interface FilterConfig {
    component: ComponentType<any>;
    /**
     * Default selected values when filter is initialized.
     * Empty array indicates no default selections for multi-select filters.
     */
    defaultValue: string[];
    /**
     * Human-readable label displayed in the UI.
     */
    label: string;
    /**
     * Unique identifier for the filter field.
     * Must match one of the predefined FilterName values.
     */
    name: string;
    /**
     * Callback function triggered on value changes.
     * Receives the new selected value(s) as parameter.
     */
    onChange(value: any): void;
    /**
     * Available options for selection within this filter.
     * Array of label-value pairs for dropdown presentation.
     */
    options: {
        color?: ThemeColor;
        label: string;
        value: string;
    }[];
    tagConfig: {
        isDeletable: boolean;
        label(
            props: FilterConfig,
            choosedOption?: {
                color?: ThemeColor;
                label: string;
                value: string;
            }
        ): string;
        name: string;
    };
    /**
     * Type of filter input component to render.
     * Determines the interaction pattern and data structure.
     */
    type: 'multi-select' | 'single-select' | 'time-select';
}

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
export interface ComponentProps {
    /**
     * Array of filter configuration objects that define the available filters and their behavior.
     * Each configuration specifies the filter type, options, default values, and change handlers for managing dashboard or data table filtering.
     * The component will render appropriate input controls based on the filter configuration provided.
     */
    filter: FilterConfig[];
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable test automation.
     * Used as the data-cy attribute value for component selection in Cypress tests.
     *
     * @default 'FilterWrapper'
     */
    testId?: string;
}

/**
 * The `FilterWrapper` component provides a comprehensive filtering interface for dashboard views and data tables.
 * It renders a form-based container that manages multiple filter inputs, displays active filter tags, and handles filter state management.
 * The component supports both single-select and multi-select filter types with dynamic rendering based on configuration.
 *
 * @param props          The component props.
 * @param props.children Child components to be processed and rendered alongside filter components.
 * @param props.filter   Array of filter configurations that define the available filters and their behavior.
 * @param props.testId   Test identifier for component testing and automation purposes.
 * @returns The FilterWrapper component JSX containing form, filters, and filter tags.
 *
 * @example
 * ```tsx
 * <FilterWrapper
 *     filter={[
 *         {
 *             name: 'status',
 *             label: 'Status',
 *             type: 'multi-select',
 *             defaultValue: [],
 *             options: [{label: 'Active', value: 'active'}],
 *             onChange: (value) => console.log(value)
 *         }
 *     ]}
 *     testId="dashboard-filters"
 * >
 *     <Button>Apply Filters</Button>
 * </FilterWrapper>
 * ```
 */
const FilterWrapper = ({children, filter, testId = 'FilterWrapper'}: WithChildren<ComponentProps>) => {
    const {filterComp} = useFilterWrapper(filter);

    return (
        <Form data-cy={testId} id="Filter" onSubmit={async () => {}}>
            <StyledRow align="center">
                {processChildren(children, filterComp)}
            </StyledRow>
            <FilterTags filter={filter} />
        </Form>
    );
};

FilterWrapper.displayName = 'FilterWrapper';

export {FilterWrapper};

const StyledRow = styled(Row)`
    flex: 0 0 auto;
`;